import { Button } from '@/components/Buttons';
import { BUTTON_LABELS, FORM_LABELS, MANAGER_LABELS, TABLE_LABELS } from '@/constants';
import { formatDate } from '@/utils';
import { handleManagerSharedCallbackClick } from '@/features/managed-user-dashboard/utils/managed-user-shared-callbacks';
import { handleManagerSharedProductChangeCallback } from '@/features/managed-user-dashboard/utils/managed-user-shared-callbacks';
import { IManagedUserSession } from '@/types/manager';
import { ITableColumn, Table } from '@/components/Tables/Table';
import { ManagedUserTabTableProps as Props } from '@/features/managed-user-dashboard';
import { ManagerTableCellLabel } from '@/components/Tables/ManagerTablesComponents';
import { NativeSelect } from '@/components/Fields';
import { PassFailTag } from '@/components/Tags';
import { TextSplitMap } from '@/components/Text';
import { useManagerActiveProduct } from '@/features/management';
import React, { useMemo } from 'react';

export const ManagedUserTabTablePractices: React.FC<Props> = React.memo(({
  activeProduct,
  isLoading = false,
  managedUser,
  onProductChange,
  onViewScorecardClick,
  productOptions = [],
}) => {
  /**
   * Currently active product option.
   */
  const activeProductOption = useManagerActiveProduct({ productOptions, activeProduct });

  /**
   * Memoized placeholder text for the product select dropdown. If an active product option
   * is selected, the placeholder will prompt the user to change the product. If no product
   * is selected, it will prompt the user to select a product.
   */
  const productPlaceholder = useMemo(() => {
    return activeProductOption ? FORM_LABELS.PRODUCT_CHANGE : FORM_LABELS.PRODUCT_SELECT;
  }, [activeProductOption]);

  /**
   * Memoized table data for practice certifications. Filters the managedUser's practice certificates
   * based on the active product selection (if any). Returns an empty array if managedUser or
   * practice certificates are undefined.
   */
  const tableData: IManagedUserSession[] = useMemo(() => {
    if (!managedUser) return []; // Return empty array if managedUser is undefined
    if (!managedUser.practiceCertificates) return []; // Return empty array if practice certificates is undefined

    // Filter practice certificates based on active product selection, or all if no product is selected
    return managedUser.practiceCertificates.filter((cert) => {
      return !activeProductOption
        ? true
        : cert.certificationId.toString() === activeProductOption.value;
    });
  }, [managedUser, activeProductOption]);

  /**
   * Callbacks object that maps callback names to their respective handler functions. This is used to
   * pass the appropriate callback functions to child components without having to define them inline,
   * which can help with performance and readability.
   */
  const callbacks = {
    handleScorecardClick: handleManagerSharedCallbackClick,
    handleProductChange: handleManagerSharedProductChangeCallback,
  };

  /**
   * Table column definitions for the practice certifications table. Defines columns for
   * Product, Status, Date Completed, and Actions.
   */
  const tableColumns: ITableColumn[] = [
    {
      key: 'certificationTitleShort',
      title: TABLE_LABELS.PRODUCT,
      width: 145,
      render: (product: string) => <ManagerTableCellLabel label={product} />,
    },
    {
      key: 'status',
      title: TABLE_LABELS.STATUS,
      width: 240,
      render: (_, record: IManagedUserSession) => {
        return (
          <div className="flex items-center justify-start gap-2">
            <PassFailTag
              className="antialiased"
              simulationType="practice"
              variant={
                record.attemptCount === 0 ? 'not attempted' : record.passed ? 'pass' : 'fail'
              }
            />
          </div>
        );
      },
    },
    {
      key: 'submittedAt',
      title: TABLE_LABELS.DATE_COMPLETED,
      width: 355,
      render: (date: string) => (
        <ManagerTableCellLabel label={date ? formatDate(date, 'shortDate') : TABLE_LABELS.NA} />
      ),
    },
    {
      key: 'actions',
      title: TABLE_LABELS.ACTIONS,
      headerClassName: 'opacity-0',
      render: (_, record) => (
        <Button
          label={BUTTON_LABELS.VIEW_SCORECARD}
          onClick={() => callbacks.handleScorecardClick({ record, callback: onViewScorecardClick })}
          size="small"
          variant="link"
        />
      ),
    },
  ];

  return (
    <div>
      <div>
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-2xl font-normal text-neutral-black">
            {MANAGER_LABELS.PRACTICE_CERTIFICATION_SCORECARDS}
          </h2>
          <NativeSelect
            id="product-select-practices"
            isLoading={isLoading}
            name="Product Select"
            onChangeCallback={(value) => callbacks.handleProductChange(value, onProductChange)}
            options={productOptions}
            placeholder={productPlaceholder}
            value={activeProductOption?.value || 'all'}
          />
        </div>

        <div className="mb-6 text-sm leading-relaxed text-neutral-black">
          <TextSplitMap text={MANAGER_LABELS.MANAGED_USER_DESCRIPTION_PRACTICES} className="mb-1" />
        </div>
      </div>

      <Table columns={tableColumns} data={tableData} loading={isLoading} />
    </div>
  );
});

ManagedUserTabTablePractices.displayName = 'ManagedUserTabTablePractices';

export default ManagedUserTabTablePractices;
